/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import {
  AlertComponent,
  BreadcrumbComponent,
  PaginationComponent,
} from "../../../common/components";
import CreateLeadCapturePopupConfirmation from "./qr-code-popups/create-lead-capture-popup-confirmation/view-qr-code-popups";
import { breadcrumbLink } from "./qr-code-list.constant";
import ORCodeFilter from "./qr-code-filter/qr-code-filter";
import MUITable from "../../../common/components/mui-table/mui-table";
import "./qr-code-list.scss";
import { getQRCode } from "../../../api/surveys";
import { useSetLoading } from "../../../common/hooks/appearance";
import ConfirmationPopup from "../../../common/components/confirmation-popup/confirmation-popup";
import { formatDate } from "../../../utils/utils";
import { useLogin } from "../../../common/hooks/useUser";
import { actionOptions } from "./qr-code-list.constant";
import { formatTypeWithRegex } from "../../../utils/utils";

const QRCodeList = () => {
  const [crumbMappingOption, setCrumbMappingOption] =
    useState<any>(breadcrumbLink);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedQRCodeId, setSelectedQRCodeId] = useState<number | null>(null);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [totalRecords, setTotalRecords] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
    const [isShowConfirmationPopup, setIsShowConfirmationPopup] = useState(false);
  const [QRCodeUrlSelected, setQRCodeUrlSelected] = useState<string | null>(null);
  const isSearchTriggered = useRef(false);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    setCurrentPage(1);
  };
  

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      setSearchText(text);
      isSearchTriggered.current = true;
      setCurrentPage(1);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    setSelectedType("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
    handleSearch("");
  };

  const loginDetails = useLogin();
  const isEmailConfigured = loginDetails?.isEmailConfigured;

  const fetchQRCode = async (
    page = 1,
    search: string = "",
    category: string = "",
  ) => {
    setLoading(true);
    setCrumbMappingOption(breadcrumbLink);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (search) {
        params.searchText = search;
      }
      if (category) {
        params.category = category;
      }

      const response = await getQRCode (params);
      setData(response.data.data.data);
      setTotalRecords(response.data.data.total);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns"
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
   const handleAction = (actionType: string, id: number) => {
  setSelectedQRCodeId(id);
  const selectedQRCode = data?.find((item) => item.id === id);
  
  if (actionType === "View") {
    if (selectedQRCode?.qr_code_url) {
      handleViewClick(selectedQRCode.qr_code_url);
    }
  }
  
  if (actionType === "Download") {
    if (selectedQRCode?.qr_code_url) {
      handleDownload(selectedQRCode.qr_code_url);
    }
  }
};
    const handleDownload = async (imageUrl: any) => {
    if (!imageUrl) return;
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = 'qr-code.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(imageUrl);
  };

  useEffect(() => {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchQRCode(pageToUse, searchText, selectedType);
      isSearchTriggered.current = false;
  }, [currentPage, searchText, selectedType]);

  const handleViewClick = async (QRUrl: string) => {
    setIsShowConfirmationPopup(true);
    setQRCodeUrlSelected(QRUrl);
  };

  const headers = [
      {
        key: "action",
        label: "Actions",

      },
    { key: "qrCodeId", label: "ID" },
    { key: "formTitle", label: "Name" },
    { key: "type", label: "Type" },
    { key: "createdOn", label: "Date Generated" }, 
    ];
  
    const formattedData = useMemo(() => {
      if (Array.isArray(data)) {
        return data.map((item: any) => {
          return {
            id: item.id,
            qrCodeId: `QR-${item.id || "00"}`,
            formTitle: item.form_title || "-",
              type: formatTypeWithRegex(item.resource_name),
            status: item.is_active ? "Active" : "Inactive",
             createdOn: formatDate(item.updated_at) || "-",
          };
        });
      }
      return [];
    }, [data]);

  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading QR Codes...";
    }
    if (searchText) {
      return "No QR Codes found matching your search";
    }
    return "No QR Codes available";
  }, [loading, searchText]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="qr-code-list-wrapper">
        <BreadcrumbComponent
          breadcrumbLink={crumbMappingOption}
          showBackArrow={false}
        />

        <div>
           <ORCodeFilter
            onSearch={handleSearch}
            searchRef={searchRef}
            isResetSearch={isResetSearch}
            resetSearch={resetSearch}
            onTypeChange={handleTypeChange}
            selectedType={selectedType}
          />

          <div className="qr-code-table-filter">
            <div className="qr-code-table">
              <MUITable
                  headers={headers}
                    data={formattedData?.map((item: any) => ({
                    id: item?.id,
                    qrCodeId: item?.qrCodeId,
                    action: "verticalEllipse",
                    formTitle: item?.formTitle,
                    type: item?.type,
                    createdOn: item?.createdOn,
                  }))}
                  actionOptions={actionOptions ? actionOptions() : []}
                  noResultsTxt={noResultText}
                  handleAction={handleAction}
                />
            </div>
            {isShowConfirmationPopup ? (
              <div>
                <CreateLeadCapturePopupConfirmation
                  isShowConfirmationPopup={isShowConfirmationPopup}
                  setIsShowConfirmationPopup={setIsShowConfirmationPopup}
                  QRCodeUrlSelected={QRCodeUrlSelected}
                  setQRCodeUrlSelected={setQRCodeUrlSelected}
                />
              </div>
            ) : (
              ""
            )}
            {totalRecords && totalRecords > 0 ? (
              <div className="qr-code-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>

      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />

    </>
  );
};

export default QRCodeList;